import React, { useState, useEffect } from 'react';
import { CheckOutlined } from '@ant-design/icons';
import CheckIcon from './CheckIcon';
import './TemplateSelector.scss';
import errImg from '@/assets/images/usual/errImg.webp';

const TemplateSelector = ({
    templates = [],
    colorOptions = [],
    styleOptions = [],
    onColorSelect,
    onStyleSelect,
    onConfirm, // 🔥 新增：确认回调
    selectedTemplate = null,
    selectedColor = 2,
    selectedStyle = '科技风',
    // 🔥 新增：历史数据支持
    historyData = null,
    isHistoryMode = false,
    // 🔥 修改：使用recordId来获取独立的加载状态
    recordId = null,
    loadingCovers = {},
    // 🔥 新增：聊天相关数据
    message = null,
    chatHistory = [],
    // 🔥 新增：完成状态，表示这一步已经完成，组件变为不可点击
    isCompleted = false
}) => {
    // 🔥 新增：从历史消息中获取已选择的模板数据
    const getSelectedTemplateFromHistory = () => {
        if (!chatHistory || chatHistory.length === 0) return null;

        // 获取当前消息的 section_id（同一轮对话的标识）
        const currentSectionId = message?.section_id;
        if (!currentSectionId) return null;

        // 在同一轮对话中查找 aippt_cover 类型的消息
        const coverMessage = chatHistory.find((msg) => msg.section_id === currentSectionId && msg.content_type === 'aippt_cover');

        return coverMessage || null;
    };
    // 使用传入的主题色选项，如果没有则使用默认值
    const themeColors =
        colorOptions.length > 0
            ? colorOptions
            : [
                  { id: 0, color: '#1890FF', name: '蓝色' },
                  { id: 1, color: '#F5222D', name: '红色' },
                  { id: 2, color: '#FA8C16', name: '橙色' },
                  { id: 3, color: '#FAAD14', name: '黄色' },
                  { id: 4, color: '#52C41A', name: '绿色' },
                  { id: 5, color: '#13C2C2', name: '青色' },
                  { id: 6, color: '#722ED1', name: '紫色' },
                  { id: 7, color: '#FF85C0 ', name: '粉色' }
              ];

    // 颜色名称到ID的映射函数
    const getColorIdByName = (colorName) => {
        const colorItem = themeColors.find((item) => item.name === colorName);
        return colorItem ? colorItem.id : selectedColor;
    };

    // 🔥 历史模式下使用历史数据的颜色和风格
    const initialColor = isHistoryMode && historyData ? getColorIdByName(historyData.color) : selectedColor;

    const initialStyle = isHistoryMode && historyData && historyData.style ? historyData.style : selectedStyle;

    // 🔥 修改：获取历史中的选中模板消息
    const historySelectedMessage = getSelectedTemplateFromHistory();
    const [localSelectedTemplate, setLocalSelectedTemplate] = useState(historySelectedMessage || selectedTemplate);

    const [localSelectedColor, setLocalSelectedColor] = useState(initialColor);
    const [localSelectedStyle, setLocalSelectedStyle] = useState(initialStyle || selectedStyle);

    // 🔥 新增：处理中状态，当用户点击模板后禁用整个组件
    const [isProcessing, setIsProcessing] = useState(false);

    // 🔥 修改：检查是否应该禁用整个组件（完成状态、处理中状态或正在加载封面）
    const isLoadingCover = recordId && loadingCovers && loadingCovers[recordId];
    const isDisabled = isCompleted || isProcessing || isLoadingCover;

    // 🔥 新增：监听加载状态变化，派发相应事件
    useEffect(() => {
        const sessionId = localStorage.getItem('aippt_prev_session_id');

        if (isLoadingCover) {
            // 开始加载封面时派发加载开始事件
            window.dispatchEvent(
                new CustomEvent('aippt-loading-start', {
                    detail: {
                        sessionId: sessionId,
                        timestamp: Date.now(),
                        type: 'cover_loading', // 标记为封面加载
                        recordId: recordId
                    }
                })
            );
        } else if (recordId && loadingCovers && !loadingCovers[recordId]) {
            // 封面加载完成时派发加载完成事件
            window.dispatchEvent(
                new CustomEvent('aippt-loading-complete', {
                    detail: {
                        sessionId: sessionId,
                        timestamp: Date.now(),
                        type: 'cover_loading_complete', // 标记为封面加载完成
                        recordId: recordId
                    }
                })
            );
        }
    }, [isLoadingCover, recordId, loadingCovers]);

    // 🔥 新增：监听处理状态变化，派发相应事件
    useEffect(() => {
        const sessionId = localStorage.getItem('aippt_prev_session_id');

        if (isProcessing) {
            // 开始处理时派发加载开始事件
            window.dispatchEvent(
                new CustomEvent('aippt-loading-start', {
                    detail: {
                        sessionId: sessionId,
                        timestamp: Date.now(),
                        type: 'template_processing', // 标记为模板处理中
                        recordId: recordId
                    }
                })
            );
        } else {
            // 处理完成时派发加载完成事件
            window.dispatchEvent(
                new CustomEvent('aippt-loading-complete', {
                    detail: {
                        sessionId: sessionId,
                        timestamp: Date.now(),
                        type: 'template_processing_complete', // 标记为模板处理完成
                        recordId: recordId
                    }
                })
            );
        }
    }, [isProcessing, recordId]);

    // 🔥 确保初始状态正确设置
    useEffect(() => {
        if (initialStyle || selectedStyle) {
            const targetStyle = initialStyle || selectedStyle;
            if (localSelectedStyle !== targetStyle) {
                setLocalSelectedStyle(targetStyle);
            }
        }
    }, [initialStyle, selectedStyle]);

    // 🔥 监听historyData变化，更新本地选中状态
    useEffect(() => {
        if (isHistoryMode && historyData) {
            const newColor = getColorIdByName(historyData.color);
            const newStyle = historyData.style;

            if (newColor !== localSelectedColor) {
                setLocalSelectedColor(newColor);
            }
            // 🔥 修复：只有当newStyle有值时才更新，避免覆盖为undefined
            if (newStyle && newStyle !== localSelectedStyle) {
                setLocalSelectedStyle(newStyle);
            }
        }
    }, [historyData?.color, historyData?.style, isHistoryMode]);

    // 使用传入的风格选项，如果没有则使用默认值
    const styles =
        styleOptions.length > 0
            ? styleOptions.map((style, index) => ({
                  id: index,
                  name: style
              }))
            : [
                  { id: 'tech', name: '科技风' },
                  { id: 'business', name: '商务' },
                  { id: 'fresh', name: '小清新' },
                  { id: 'minimal', name: '极简' },
                  { id: 'chinese', name: '中国风' },
                  { id: 'cute', name: '可爱卡通' }
              ];

    // 处理模板选择
    const handleTemplateSelect = (template) => {
        // 🔥 如果已完成或正在处理中，不允许点击
        if (isDisabled) return;

        setLocalSelectedTemplate(template);

        // 🔥 设置处理中状态，禁用整个组件
        setIsProcessing(true);

        // 🔥 新增：模板选择时派发加载开始事件，通知WebSender显示暂停图标
        window.dispatchEvent(
            new CustomEvent('aippt-loading-start', {
                detail: {
                    sessionId: localStorage.getItem('aippt_prev_session_id'),
                    timestamp: Date.now(),
                    type: 'template_select', // 标记为模板选择
                    recordId: recordId,
                    templateId: template.id || template.cover_id
                }
            })
        );

        // 🔥 新增：选择模板后立即触发确认回调，开始生成PPT
        if (onConfirm) {
            onConfirm({
                template: template,
                color: localSelectedColor,
                style: localSelectedStyle
            });
        }
    };

    // 处理颜色选择
    const handleColorSelect = (colorId) => {
        // 🔥 如果已完成或正在处理中，不允许点击
        if (isDisabled) return;

        setLocalSelectedColor(colorId);

        // 🔥 新增：颜色选择时派发加载开始事件，通知WebSender显示暂停图标
        window.dispatchEvent(
            new CustomEvent('aippt-loading-start', {
                detail: {
                    sessionId: localStorage.getItem('aippt_prev_session_id'),
                    timestamp: Date.now(),
                    type: 'color_select', // 标记为颜色选择
                    recordId: recordId
                }
            })
        );

        // 🔥 修复：在历史模式下传递historyData
        onColorSelect && onColorSelect(colorId, { historyData, isHistoryMode });
    };

    // 处理风格选择
    const handleStyleSelect = (style) => {
        // 🔥 如果已完成或正在处理中，不允许点击
        if (isDisabled) return;

        setLocalSelectedStyle(style);

        // 🔥 新增：风格选择时派发加载开始事件，通知WebSender显示暂停图标
        window.dispatchEvent(
            new CustomEvent('aippt-loading-start', {
                detail: {
                    sessionId: localStorage.getItem('aippt_prev_session_id'),
                    timestamp: Date.now(),
                    type: 'style_select', // 标记为风格选择
                    recordId: recordId
                }
            })
        );

        // 🔥 修复：在历史模式下传递historyData
        onStyleSelect && onStyleSelect(style, { historyData, isHistoryMode });
    };

    // 处理图片加载失败
    const handleImageError = (e) => {
        if (e.target.src !== errImg) {
            e.target.src = errImg;
        }
    };

    return (
        <div
            className={`template-selector ${isDisabled ? 'disabled' : ''} ${isProcessing ? 'processing' : ''} ${isCompleted ? 'completed' : ''}`}
            data-component="template-selector"
        >
            {/* 标题和介绍 */}
            <div className="template-header">
                <p className="template-intro">参照你的主题开始为你实时渲染相符合的演示风格页面，请选择最心仪的一款，开启高效创作！</p>
            </div>

            {/* 模板预览区域 */}
            <div className="template-preview-section">
                {/* 🔥 新增：如果正在加载封面，显示加载状态 */}
                {recordId && loadingCovers[recordId] ? (
                    <div className="loading-cover-section">
                        <div className="loading-box">
                            <div className="loading-spinner"></div>
                            <div className="loading-text">正在生成新的封面...</div>
                        </div>
                    </div>
                ) : /* 🔥 新增：如果是历史模式，显示历史封面 */
                (() => {
                      const shouldShowHistory = isHistoryMode && historyData?.cover_image_list;
                      return shouldShowHistory;
                  })() ? (
                    <div className="history-covers-section">
                        <div className="template-grid">
                            {historyData.cover_image_list.map((cover, index) => (
                                <div
                                    key={cover.cover_id || index}
                                    className={`template-card ${historyData?.cover_id === cover.cover_id || (isDisabled && (localSelectedTemplate?.cover_id === cover.cover_id || selectedTemplate?.cover_id === cover.cover_id)) ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                    onClick={() => handleTemplateSelect(cover)}
                                >
                                    <div className="template-image">
                                        <img src={cover.cover_image} alt={`封面 ${index + 1}`} onError={handleImageError} />
                                        <div className="template-overlay">
                                            <div className="template-info"></div>
                                        </div>
                                    </div>
                                    {(historySelectedMessage?.content?.cover_id === cover.cover_id ||
                                        (isDisabled &&
                                            (localSelectedTemplate?.cover_id === cover.cover_id ||
                                                selectedTemplate?.cover_id === cover.cover_id))) && (
                                        <div className="selection-indicator">
                                            <CheckIcon className="checkmark" size={30} />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    // 原始模板选择区域
                    <div className="template-grid">
                        {templates.map((template, index) => (
                            <div
                                key={template.id}
                                className={`template-card ${localSelectedTemplate?.id === template.id || (isDisabled && selectedTemplate?.id === template.id) ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                onClick={() => handleTemplateSelect(template)}
                            >
                                <div className="template-image">
                                    <img src={template.preview} alt={template.name} onError={handleImageError} />
                                    <div className="template-overlay">
                                        <div className="template-info">
                                            <div className="template-brand">
                                                <span className="brand-ppt">PPT</span>
                                                <span className="brand-islide">iSlide</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {(localSelectedTemplate?.id === template.id || (isDisabled && selectedTemplate?.id === template.id)) && (
                                    <div className="selection-indicator">
                                        <CheckIcon className="checkmark" size={14} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* 主题色选择 */}
            <div className="color-section">
                <h3 className="section-title">主题色</h3>
                <div className="color-palette">
                    {themeColors.map((color) => (
                        <div
                            key={color.id}
                            className={`color-swatch ${localSelectedColor === color.id ? 'selected' : ''}`}
                            onClick={() => handleColorSelect(color.id)}
                            style={{ backgroundColor: color.color }}
                            title={color.name}
                        >
                            {localSelectedColor === color.id && <CheckIcon className="color-checkmark" size={30} />}
                        </div>
                    ))}
                </div>
            </div>

            {/* 风格选择 */}
            <div className="style-section">
                <h3 className="section-title">风格</h3>
                <div className="style-options">
                    {styles.map((style) => {
                        // 🔥 修复：简化风格选中逻辑，避免出现多个高亮
                        const isSelected = localSelectedStyle === style.name;
                        return (
                            <button
                                key={style.id}
                                className={`style-button ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                onClick={() => handleStyleSelect(style.name)}
                                disabled={isDisabled}
                            >
                                {style.name}
                                {isSelected && <CheckIcon className="style-checkmark" size={20} useThemeColor={true} />}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default TemplateSelector;
